import type { UserFeatureToggle, UserResponse } from '@/clients/UserClient/types';
import { userClient } from '@/clients/UserClient/UserClient';
import { queryClient } from 'config/queryClient';
import { QueryKey } from '@/types/query';
import { useMutation } from '@tanstack/react-query';

import { useCurrentUserQuery } from './use-current-user-query';

export const useUpdateUserFeatureTogglesMutation = () => {
  const { data: currentUser } = useCurrentUserQuery();

  return useMutation({
    mutationFn: async (userFeatureToggles: Partial<UserFeatureToggle>[]) => {
      const existingFeatures = currentUser?.features ?? [];

      const hasChanged = userFeatureToggles.some((toggle) => {
        const existingToggle = existingFeatures.find((f) => f.name === toggle.name);
        if (!existingToggle) return true;
        if (existingToggle.value !== toggle.value) return true;
        if (existingToggle.enabled !== toggle.enabled && toggle.enabled !== undefined) return true;
        return false;
      });

      if (!hasChanged) {
        return currentUser;
      }

      const newFeatures = existingFeatures
        .filter((f) => !userFeatureToggles.some((uf) => uf.name === f.name))
        .concat(userFeatureToggles as UserFeatureToggle[]);

      const { data } = await userClient.updateCurrentUser({ features: newFeatures });

      return data;
    },
    onSuccess: (userResponse) => {
      if (!userResponse) return;
      queryClient.setQueryData<UserResponse>([QueryKey.useCurrentUserQuery], (currentData) => {
        if (!currentData) return currentData;

        return { ...currentData, features: userResponse.features };
      });
    },
  });
};
